'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import {
  DASHBOARD_AGENDA_BADGE_EXPECT,
  DASHBOARD_AGENDA_BADGE_SCHED,
  DASHBOARD_AGENDA_BADGE_SENT,
  DASHBOARD_AGENDA_EMPTY,
  DASHBOARD_AGENDA_MAINT_EXP_TITLE,
  DASHBOARD_AGENDA_MAINT_SCHED_TITLE,
  DASHBOARD_AGENDA_SEND_TITLE,
  DASHBOARD_AGENDA_SCROLL_NEXT,
  DASHBOARD_AGENDA_SCROLL_PREV,
  DASHBOARD_AGENDA_TITLE,
} from '@/features/app/dashboard/presentation/constants/dashboard-copy';
import {
  buildWeekAgenda,
  localThisAndPrevWeekBounds,
  type WeekAgendaItem,
  type WeekAgendaKind,
  weekDayKeysFromMonday,
  weekRangeLabelPt,
  weekdayShortLabelPt,
} from '@/features/app/dashboard/presentation/utils/dashboard-metrics';

function agendaTitle(kind: WeekAgendaKind): string {
  switch (kind) {
    case 'send':
      return DASHBOARD_AGENDA_SEND_TITLE;
    case 'maintenance_scheduled':
      return DASHBOARD_AGENDA_MAINT_SCHED_TITLE;
    default:
      return DASHBOARD_AGENDA_MAINT_EXP_TITLE;
  }
}

function agendaBadge(kind: WeekAgendaKind): { label: string; className: string } {
  switch (kind) {
    case 'send':
      return {
        label: DASHBOARD_AGENDA_BADGE_SENT,
        className: 'bg-emerald-50 text-emerald-800',
      };
    case 'maintenance_scheduled':
      return {
        label: DASHBOARD_AGENDA_BADGE_SCHED,
        className: 'bg-emerald-50 text-emerald-800',
      };
    default:
      return {
        label: DASHBOARD_AGENDA_BADGE_EXPECT,
        className: 'bg-amber-50 text-amber-800',
      };
  }
}

function groupByDay(dayKeys: string[], items: WeekAgendaItem[]): Map<string, WeekAgendaItem[]> {
  const map = new Map<string, WeekAgendaItem[]>();
  for (const k of dayKeys) map.set(k, []);
  for (const it of items) {
    const list = map.get(it.dayKey);
    if (list) list.push(it);
  }
  return map;
}

export interface DashboardWeekAgendaProps {
  reminders: Reminder[];
  getClientName: (clientId: string) => string;
}

export const DashboardWeekAgenda: React.FC<DashboardWeekAgendaProps> = ({ reminders, getClientName }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const { dayKeys, rangeLabel, items } = useMemo(() => {
    const { thisStart, thisEnd } = localThisAndPrevWeekBounds();
    const keys = weekDayKeysFromMonday(thisStart);
    const built = buildWeekAgenda(reminders, thisStart, thisEnd, getClientName);
    return {
      dayKeys: keys,
      rangeLabel: weekRangeLabelPt(thisStart, thisEnd),
      items: built,
    };
  }, [reminders, getClientName]);

  const byDay = useMemo(() => groupByDay(dayKeys, items), [dayKeys, items]);

  const updateScrollButtons = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 2) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    setCanScrollPrev(scrollLeft > 4);
    setCanScrollNext(scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    const ro = new ResizeObserver(() => updateScrollButtons());
    ro.observe(el);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons, dayKeys]);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.min(Math.max(el.clientWidth * 0.75, 200), 320);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section className="rounded-xl border border-border-light bg-white p-4 shadow-sm sm:p-5 min-w-0">
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h3 className="font-semibold text-navy-dark flex items-center gap-2">
          <AppIcon name="CalendarDaysIcon" size={20} className="text-cyan shrink-0" />
          {DASHBOARD_AGENDA_TITLE}
        </h3>
        <p className="text-xs text-text-muted sm:text-sm tabular-nums">{rangeLabel}</p>
      </div>

      <div className="flex items-stretch gap-1.5 min-w-0 sm:gap-2">
        <button
          type="button"
          className="lg:hidden shrink-0 self-center flex h-10 w-10 items-center justify-center rounded-lg border border-border-light bg-white text-navy-dark shadow-sm disabled:opacity-35 disabled:pointer-events-none"
          aria-label={DASHBOARD_AGENDA_SCROLL_PREV}
          disabled={!canScrollPrev}
          onClick={() => scrollByDir(-1)}
        >
          <AppIcon name="ChevronLeftIcon" size={22} className="text-navy-dark" />
        </button>

        <div
          ref={scrollerRef}
          className="scrollbar-hide flex min-w-0 flex-1 gap-3 overflow-x-auto lg:grid lg:grid-cols-7 lg:gap-3 lg:overflow-visible snap-x snap-mandatory lg:snap-none"
        >
          {dayKeys.map((dayKey, colIdx) => {
            const dayItems = byDay.get(dayKey) ?? [];
            return (
              <div
                key={dayKey}
                className="min-w-[148px] shrink-0 snap-start lg:min-w-0 flex flex-col border border-border-light rounded-lg bg-[#fafbfc] p-2 sm:p-2.5"
              >
                <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-text-muted mb-2 sm:text-xs">
                  {weekdayShortLabelPt(dayKey)}
                </p>
                <div className="flex flex-col gap-2 flex-1 min-h-[4rem]">
                  {dayItems.length === 0 ? (
                    <p className="text-[10px] text-text-muted/80 text-center py-3 sm:text-xs">—</p>
                  ) : (
                    dayItems.map((entry, i) => {
                      const badge = agendaBadge(entry.kind);
                      const accent = (colIdx + i) % 2 === 0 ? 'border-l-navy' : 'border-l-cyan';
                      return (
                        <article
                          key={entry.id}
                          className={`rounded-md border border-border-light bg-white pl-2 pr-2 py-2 shadow-sm border-l-4 ${accent}`}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <p className="text-xs font-bold text-navy-dark tabular-nums leading-none">
                              {entry.timeLabel}
                            </p>
                          </div>
                          <p className="text-xs font-semibold text-navy-dark mt-1 leading-snug line-clamp-2">
                            {agendaTitle(entry.kind)}
                          </p>
                          <p className="text-[10px] text-text-muted mt-0.5 truncate sm:text-xs">{entry.clientName}</p>
                          <div className="flex justify-end mt-1.5">
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full sm:text-xs ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="lg:hidden shrink-0 self-center flex h-10 w-10 items-center justify-center rounded-lg border border-border-light bg-white text-navy-dark shadow-sm disabled:opacity-35 disabled:pointer-events-none"
          aria-label={DASHBOARD_AGENDA_SCROLL_NEXT}
          disabled={!canScrollNext}
          onClick={() => scrollByDir(1)}
        >
          <AppIcon name="ChevronRightIcon" size={22} className="text-navy-dark" />
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-text-muted text-center mt-4 pt-4 border-t border-border-light">
          {DASHBOARD_AGENDA_EMPTY}
        </p>
      ) : null}
    </section>
  );
};
